import { type ReactElement } from "react";

import { Column, Table } from "@snapchat/snap-design-system";

import { type MetricField } from "../../../gql-generated/types";
import { columnCellRenderer } from "../../../shared/utils/table";
import { funnelAnalysisFieldsWithDimensionsCascadingOrder } from "../../models";
import { Dimension, type DimensionFunnelMap, NOT_ENOUGH_MESSAGE, metricsLabels } from "../../models/dimension-funnel";

import { EngagementBreakdownCell } from "./EngagementBreakdownCell";
import { type FunnelSlicePath } from "./makeDimensionLabels";

const columnFields: ("User Type" | MetricField)[] = ["User Type", ...funnelAnalysisFieldsWithDimensionsCascadingOrder];

export const createTable =
    (dimensionFunnelMap: DimensionFunnelMap, dimensionLabels: [FunnelSlicePath, string][]) =>
    ({ width }: { width: number }): ReactElement => {
        return (
            <Table rowHeight={40} rowsCount={dimensionLabels.length} headerHeight={40} width={width} maxHeight={580}>
                {columnFields.map((metricFieldOrLabel) => {
                    if (metricFieldOrLabel === "User Type") {
                        return (
                            <Column
                                key={"USER_TYPE"}
                                header={
                                    <EngagementBreakdownCell
                                        text={metricFieldOrLabel}
                                        isBold={true}
                                        isFlexStart={true}
                                    />
                                }
                                cell={columnCellRenderer(dimensionLabels, ({ value, rowIndex, ...props }) => (
                                    <EngagementBreakdownCell
                                        text={value[1]}
                                        rowIndex={rowIndex}
                                        isFlexStart={true}
                                        isHighlighted={value[0][0] === Dimension.TOTAL}
                                        {...props}
                                    />
                                ))}
                                width={50}
                                flexGrow={1}
                            />
                        );
                    }

                    return (
                        <Column
                            key={metricFieldOrLabel}
                            header={
                                <EngagementBreakdownCell
                                    text={metricsLabels[metricFieldOrLabel] ?? "-"}
                                    isBold={true}
                                />
                            }
                            cell={columnCellRenderer(dimensionLabels, ({ value, rowIndex, ...props }) => {
                                const [[dimension, breakdown]] = value;
                                const funnelSlice = dimensionFunnelMap[dimension]?.[breakdown];
                                const text = funnelSlice?.[metricFieldOrLabel]?.valueFormatted;

                                const subtext =
                                    text === NOT_ENOUGH_MESSAGE
                                        ? null
                                        : funnelSlice?.[metricFieldOrLabel]?.fractionFromPreviousPercentFormatted;

                                return (
                                    <EngagementBreakdownCell
                                        {...props}
                                        rowIndex={rowIndex}
                                        text={text ?? "-"}
                                        subtext={subtext ?? "-"}
                                        isHighlighted={dimension === Dimension.TOTAL}
                                    />
                                );
                            })}
                            width={50}
                            flexGrow={1}
                        />
                    );
                })}
            </Table>
        );
    };
